import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../../../api";
import toast from "react-hot-toast";
import { useCustomToast } from "../../../../components/customtoast/CustomToastProvider";
import { profile } from "../../../../api/profile";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate, reverseDate } from "./../../../../utils/dateFormatter";
import { useMutation } from "react-query";

function EditExperienceModal({ closeModal, data, refetch, refetchStatus }) {
  const { t } = useTranslation();
  const user = JSON.parse(sessionStorage.getItem("user"))?.user;
  const customToast = useCustomToast();
  const [values, setValues] = useState({
    name: data?.name,
    role: data?.role,
    start_date: reverseDate(data?.start_date),
    end_date: data?.end_date ? reverseDate(data?.end_date) : null,
    date: data?.date,
    description: data?.description,
  });

  const { isLoading, mutateAsync } = useMutation(
    profile.updateUserExperiencev2
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [isCurrentlyWorking, setIsCurrentlyWorking] = useState(
    data?.end_date === null ? true : false
  );

  const handleCurrentlyWorkingChange = (e) => {
    setIsCurrentlyWorking(e.target.checked);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });

    // Clear the validation error for the field when the user starts typing again
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDateInputChange = (name, value) => {
    setValues({ ...values, [name]: value });

    // Clear the validation error for the field when the user starts typing again
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const updateExperience = async (e) => {
    e.preventDefault();
    const requiredFields = ["name", "role", "description", "start_date"];
    const newValidationErrors = {};

    requiredFields.forEach((field) => {
      if (!values[field]) {
        newValidationErrors[field] = t(`edit-experience.${field}.required`);
      }
    });

    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      toast.loading(t("edit-experience.loading"));
      const data_ = {
        ...values,
        start_date: formatDate(values.start_date),
        end_date: formatDate(values.end_date),
      };
      const response = await mutateAsync({
        id: data.id,
        data: data_,
        userId: user?.user_id,
      });
      toast.remove();
      customToast.success(response.message);
      await refetch();
      await refetchStatus();
      closeModal(false);
      return response;
    } catch (error) {
      toast.remove();
      if (error.res) {
        customToast.error(error.res.data.message);
      } else {
        customToast.error(error.message);
      }
    }
  };

  const deleteExperience = async (e) => {
    if (confirm(t("edit-experience.delete-confirm"))) {
      try {
        toast.loading(t("edit-experience.loading"));
        const res = await profile.deleteUserExperiencev2(
          data.id,
          user?.user_id
        );
        toast.remove();
        customToast.success(res.message);
        await refetch();
        await refetchStatus();
        closeModal(false);
      } catch (error) {
        if (error.res) {
          customToast.error(error.res.data.message);
        } else {
          customToast.error(error.message);
        }
      }
    }
  };

  return (
    <div className="fixed top-10 md:top-0 left-0 w-screen h-screen grid place-items-center bg-black bg-opacity-50">
      <div className="min-h-[11rem] md:min-h-[12rem] w-[340px] md:w-[600px] bg-white rounded-lg md:rounded-[32px] flex flex-col justify-center p-8 md:p-16 relative">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          {t("edit-experience.title")}
        </h3>
        <div className="flex justify-end mb-4">
          <div
            onClick={deleteExperience}
            className="text-[#F35B24] text-xs font-medium cursor-pointer hover:bg-gray-200 hover:p-2 hover:rounded"
          >
            {t("edit-experience.remove")}
          </div>
        </div>
        <form onSubmit={updateExperience}>
          <div className="mb-4">
            <label
              htmlFor="name"
              className="text-gray-500 mb-2 text-base font-medium"
            >
              {t("edit-experience.company.label")}
              <span className="text-red-500 required-dot">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={values.name}
              onChange={handleInputChange}
              className={`rounded-lg flex-1 appearance-none border ${
                validationErrors.name ? "border-red-500" : "border-lightGrey"
              } w-full py-4 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-lightGrey focus:border-transparent`}
              name="name"
              placeholder={t("edit-experience.company.placeholder")}
            />
            {validationErrors.name && (
              <p className="text-red-500 text-xs mt-1 text-right">
                {validationErrors.name}
              </p>
            )}
          </div>

          {/* Role Field */}
          <div className="flex flex-col md:flex-row gap-x-2 mb-4">
            <div className="w-full mb-4 md:mb-0">
              <div className="flex flex-col">
                <label
                  htmlFor="role"
                  className="text-gray-500 mb-2 text-base font-medium"
                >
                  {t("edit-experience.role.label")}
                  <span className="text-red-500 required-dot">*</span>
                </label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  placeholder={t("edit-experience.role.placeholder")}
                  value={values.role}
                  onChange={handleInputChange}
                  className={`rounded-lg flex-1 appearance-none border ${
                    validationErrors.role
                      ? "border-red-500"
                      : "border-lightGrey"
                  } py-4 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-lightGrey focus:border-transparent`}
                />
                {validationErrors.role && (
                  <p className="text-red-500 text-xs mt-1 text-right">
                    {validationErrors.role}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className="mb-4 flex gap-x-4">
            <div className="mb-4 md:mb-0">
              <label
                htmlFor="start_date"
                className="text-gray-500 block mb-1 text-base font-medium"
              >
                {t("edit-experience.dates.start.label")}
                <span className="text-red-500 required-dot mr-2">*</span>
              </label>
              <DatePicker
                selected={values.start_date}
                placeholderText={t("edit-experience.dates.start.placeholder")}
                dateFormat="dd/MM/yyyy"
                className="border pr-16 border-slate-300 block w-full focus:border-primary active:border-primary py-4 px-3 rounded-lg text-sm text-slate-700"
                maxDate={values.end_date}
                onChange={(value) => handleDateInputChange("start_date", value)}
              />
              {validationErrors.start_date && (
                <p className="text-red-500 text-md mt-1 text-right">
                  {validationErrors.start_date}
                </p>
              )}
            </div>
            <div className="mb-4 md:mb-0">
              <label
                htmlFor="end_date"
                className="text-gray-500 mb-1 block text-base font-medium"
              >
                {t("edit-experience.dates.end.label")}
              </label>
              {!isCurrentlyWorking && (
                <DatePicker
                  selected={values.end_date}
                  placeholderText={t("edit-experience.dates.end.placeholder")}
                  dateFormat="dd/MM/yyyy"
                  className="border pr-16 border-slate-300 block w-full focus:border-primary active:border-primary py-4 px-3 rounded-lg text-sm text-slate-700"
                  minDate={values.start_date}
                  onChange={(value) => handleDateInputChange("end_date", value)}
                />
              )}
              <div className="mt-3">
                <label htmlFor="current-checkbox" className="flex items-center">
                  <input
                    type="checkbox"
                    id="current-checkbox"
                    checked={isCurrentlyWorking}
                    onChange={handleCurrentlyWorkingChange}
                  />
                  <span className="ml-4">
                    {t("edit-experience.dates.current")}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="mb-4">
            <label
              className="text-gray-500 mb-2 text-base font-medium"
              htmlFor="description"
            >
              {t("edit-experience.description.label")}
              <span className="text-red-500 required-dot">*</span>
            </label>
            <textarea
              value={values.description}
              onChange={handleInputChange}
              className={`mt-4 flex-1 w-full px-4 py-2 text-base text-gray-700 placeholder-gray-400 bg-white border ${
                validationErrors.description
                  ? "border-red-500"
                  : "border-lightGrey"
              } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-lightGrey focus:border-transparent`}
              id="description"
              name="description"
              placeholder={t("edit-experience.description.placeholder")}
              rows="5"
              cols="40"
            ></textarea>
            {validationErrors.description && (
              <p className="text-red-500 text-xs mt-1 text-right">
                {validationErrors.description}
              </p>
            )}
          </div>

          {/* Buttons Section */}
          <div className="flex gap-x-10 justify-center">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-primary shadow-sm px-8 py-2 md:px-16 md:py-4 bg-white text-sm md:text-lg font-medium text-darkBlue sm:ml-3 sm:w-auto sm:text-sm mb-4 sm:mb-0"
              onClick={() => closeModal(false)}
            >
              {t("edit-experience.buttons.cancel")}
            </button>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex justify-center rounded-md border border-none shadow-sm px-8 py-2 md:px-16 md:py-4 bg-primary text-sm md:text-lg font-medium text-white sm:ml-3 sm:w-auto sm:text-sm mb-4 sm:mb-0"
            >
              {t("edit-experience.buttons.update")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditExperienceModal;
